import styled from "styled-components";
import { memo, useCallback, useEffect, useState } from "react";
import {
  Plus,
  Trash2,
  Check,
  Lock,
  Loader2,
  AlertTriangle,
  Timer,
  Moon,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  BedDouble,
  Clock,
} from "lucide-react";
import { Head, router } from "@inertiajs/react";

import { theme } from "../_kit/theme";
import PageShell from "../_kit/PageShell";
import TransitPicker from "./TransitPicker";
import { useT } from "../_kit/i18n";
import {
  shiftIso,
  wibTodayIso,
  formatDate,
  wibTime,
  timeWithDayPart,
  timeCompact,
} from "../_kit/dateFormat";
import DatePickField from "../_kit/DatePickField";
import ConfirmDialog from "../_kit/ConfirmDialog";
import ReadAloudDialog from "../_kit/ReadAloudDialog";
import PhoneField from "../_kit/PhoneField";
import { apiFetch } from "../_kit/api";

const rupiah = (value) => `Rp ${Number(value || 0).toLocaleString("id-ID")}`;

const Wrap = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  /* Trailing space so the fixed bottom-right dock (max ~50vh) can never cover the submit bar — the FO
     can always scroll the submit button clear of the dock. */
  padding: 1rem 0.5rem calc(50vh + 4rem);
`;

const Panel = styled.section`
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 14px;
  padding: 1rem 1.15rem;
  margin-bottom: 1rem;
`;

const PanelTitle = styled.h2`
  margin: 0 0 0.8rem;
  font-size: 1.05rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.8rem;
`;

// Live nights count next to the date fields — recomputes from checkIn/checkOut on every keystroke so
// the FO always sees the stay length. Amber when the range is invalid (0 nights).
const NightsBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  align-self: flex-end;
  padding: 0.45rem 0.8rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 800;
  background: ${(props) => (props.$invalid ? "#fffbeb" : theme.goldSoft)};
  color: ${(props) => (props.$invalid ? "#92400e" : theme.goldDark)};
  border: 1px solid ${(props) => (props.$invalid ? "#fde68a" : theme.gold)};
`;

// Shown only between midnight and 06:00 WIB, where the day has already rolled but the front desk's
// working night has not ended. Naming today explicitly — with its day name — is what makes a wrong
// date visible before it is saved rather than after the guest has gone to bed.
const LateNightBar = styled.div`
  align-items: center;
  background: #fdf3e3;
  border: 1px solid #f2d9a8;
  border-radius: 10px;
  color: #8a5a00;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.86rem;
  font-weight: 700;
  gap: 0.5rem;
  line-height: 1.45;
  margin-bottom: 0.8rem;
  padding: 0.6rem 0.7rem;
`;

// Why the date fields are greyed out, and what to do about it. Silent disabling reads as a bug.
const DateLockNote = styled.div`
  align-items: center;
  background: #f4f2ee;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  color: #4a4f63;
  display: flex;
  font-size: 0.82rem;
  font-weight: 600;
  gap: 0.4rem;
  line-height: 1.45;
  margin-top: 0.6rem;
  padding: 0.5rem 0.65rem;
`;

const YesterdayBtn = styled.button`
  background: #8a5a00;
  border: 0;
  border-radius: 8px;
  color: #ffffff;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 800;
  padding: 0.4rem 0.7rem;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${theme.muted};
`;

const Select = styled.select`
  min-width: 220px;
  padding: 0.45rem 0.6rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${theme.ink};
`;

const NumberInput = styled.input`
  width: 90px;
  padding: 0.45rem 0.6rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.7rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 0.95rem;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  border: 0;
  border-radius: 8px;
  background: ${theme.gold};
  color: #ffffff;
  font-weight: 800;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${theme.goldDark};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const GhostButton = styled(Button)`
  background: #ffffff;
  color: ${theme.ink700};
  border: 1px solid ${theme.border};
  &:hover:not(:disabled) {
    background: ${theme.canvas};
  }
`;

const ItemCard = styled.div`
  border: 1px solid ${(props) => (props.$confirmed ? "#a7d7b0" : theme.border)};
  border-radius: 12px;
  padding: 0.9rem 1rem;
  margin-bottom: 0.8rem;
  background: ${(props) => (props.$confirmed ? "#f3fbf5" : "#ffffff")};
`;

const ItemHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  margin-bottom: 0.6rem;
`;

const ItemHeadRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ConfirmRow = styled.div`
  margin-top: 0.6rem;
`;

const GuestField = styled.label`
  display: flex;
  flex: 1 1 200px;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${theme.muted};
`;

const ItemName = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 1rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
  background: ${(props) => (props.$confirmed ? "#dcfce7" : "#eef1f5")};
  color: ${(props) => (props.$confirmed ? "#166534" : theme.muted)};
  font-size: 0.72rem;
  font-weight: 800;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.35rem 0.55rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  color: #b91c1c;
  font-weight: 700;
  font-size: 0.8rem;
  cursor: pointer;
`;

const RoomGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 0.5rem 0 0.7rem;
`;

const RoomCell = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 84px;
  padding: 0.4rem 0.55rem;
  border: 0;
  border-radius: 8px;
  background: ${(props) =>
    props.$selected ? "#c8e6c9" : props.$blocked ? "#fffbeb" : "#ffffff"};
  box-shadow: ${(props) =>
    props.$selected
      ? "inset 0 0 0 2px #4caf50"
      : props.$blocked
        ? "inset 0 0 0 1.5px #f59e0b"
        : `inset 0 0 0 1px ${theme.border}`};
  color: ${theme.ink};
  font-size: 0.9rem;
  font-weight: 800;
  cursor: pointer;
`;

// A blocked room is pickable but visibly flagged so the FO chooses it knowingly.
const BlockedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.05rem 0.32rem;
  border-radius: 5px;
  background: #f59e0b;
  color: #ffffff;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.02em;
`;

// Blue, deliberately not the amber of BlockedBadge: a day-use overlap is information, not an
// obstacle, and staff read amber as "something is wrong here".
const TransitBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.05rem 0.32rem;
  border-radius: 5px;
  background: #0284c7;
  color: #ffffff;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.02em;
`;

const TransitNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  margin-top: 0.45rem;
  padding: 0.45rem 0.6rem;
  border-radius: 8px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  color: #075985;
  font-size: 0.82rem;
  line-height: 1.4;
`;

const BlockedNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  margin-top: 0.45rem;
  padding: 0.45rem 0.6rem;
  border-radius: 8px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  font-size: 0.82rem;
  line-height: 1.4;
`;

// A checkbox drawn right on the room-number cell, so it reads as clearly pickable (ticks when chosen).
const RoomTick = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1.5px solid ${(props) => (props.$on ? "#2e7d32" : theme.border)};
  background: ${(props) => (props.$on ? "#4caf50" : "#ffffff")};
  color: #ffffff;
`;

const BedPanel = styled.div`
  margin-top: 0.7rem;
  border: 1px solid ${theme.border};
  border-radius: 10px;
  overflow: hidden;
`;

const BedHead = styled.button`
  align-items: center;
  background: #fbfaf7;
  border: 0;
  cursor: pointer;
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
  padding: 0.55rem 0.75rem;
  width: 100%;
`;

const BedHeadLeft = styled.span`
  align-items: center;
  color: ${theme.ink};
  display: inline-flex;
  font-size: 0.88rem;
  font-weight: 800;
  gap: 0.4rem;
`;

const BedHeadRight = styled.span`
  color: ${theme.ink700};
  font-size: 0.82rem;
  font-weight: 700;
`;

const BedBody = styled.div`
  border-top: 1px solid ${theme.border};
  padding: 0.7rem 0.75rem;
`;

const BedHint = styled.div`
  color: #4a4f63;
  font-size: 0.8rem;
  line-height: 1.45;
  margin-bottom: 0.55rem;
`;

const BedQuick = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.6rem;
`;

const BedQuickButton = styled.button`
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 7px;
  color: ${theme.ink700};
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 0.3rem 0.6rem;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const BedEmpty = styled.div`
  border: 1px dashed ${theme.border};
  border-radius: 8px;
  color: #5e6278;
  font-size: 0.83rem;
  padding: 0.7rem;
  text-align: center;
`;

const BedRoomBlock = styled.div`
  border: 1px solid ${theme.border};
  border-radius: 9px;
  margin-bottom: 0.5rem;
  padding: 0.55rem 0.65rem;
  &:last-of-type {
    margin-bottom: 0;
  }
`;

const BedRoomHead = styled.div`
  align-items: center;
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
  margin-bottom: 0.4rem;
`;

const BedRoomCode = styled.span`
  color: ${theme.ink};
  font-size: 0.88rem;
  font-weight: 800;
`;

const BedRoomSum = styled.span`
  color: #4a4f63;
  font-size: 0.78rem;
  font-weight: 700;
`;

const BedRow = styled.div`
  align-items: center;
  border-bottom: 1px solid ${theme.border};
  display: flex;
  gap: 0.6rem;
  justify-content: space-between;
  padding: 0.4rem 0;
  &:last-of-type {
    border-bottom: 0;
  }
`;

const BedDate = styled.span`
  color: ${theme.ink};
  font-size: 0.85rem;
  font-weight: 700;
`;

const BedStock = styled.span`
  color: ${(props) => (props.$out ? "#b91c1c" : "#5e6278")};
  font-size: 0.74rem;
  font-weight: 700;
`;

const Stepper = styled.div`
  align-items: center;
  display: inline-flex;
  gap: 0.3rem;
`;

const StepButton = styled.button`
  align-items: center;
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 6px;
  color: ${theme.ink};
  cursor: pointer;
  display: inline-flex;
  font-size: 0.95rem;
  font-weight: 800;
  height: 26px;
  justify-content: center;
  line-height: 1;
  width: 26px;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.35;
  }
`;

const StepValue = styled.span`
  color: ${theme.ink};
  font-size: 0.9rem;
  font-weight: 800;
  min-width: 1.4rem;
  text-align: center;
`;

const BedTotal = styled.div`
  border-top: 1px solid ${theme.border};
  color: ${theme.ink};
  font-size: 0.88rem;
  font-weight: 800;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  text-align: right;
`;

const RateLine = styled.div`
  font-size: 0.9rem;
  color: ${theme.ink700};
`;

const ItemError = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.5rem;
  color: #b91c1c;
  font-size: 0.85rem;
  font-weight: 700;
`;

const Muted = styled.p`
  color: ${theme.muted};
  font-size: 0.9rem;
`;

const SummaryBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  padding: 0.9rem 1.15rem;
  border-radius: 12px;
  background: ${theme.canvas};
  border: 1px solid ${theme.border};
`;

const Total = styled.div`
  font-size: 1.05rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const PageError = styled.div`
  margin-bottom: 0.8rem;
  padding: 0.6rem 0.8rem;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-weight: 700;
`;

// Live countdown for the eZee holds. Neutral, then amber near expiry, then red once lapsed — so the FO
// finishes (or the page auto-releases) before the holds silently revert.
const HoldBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.8rem;
  padding: 0.55rem 0.8rem;
  border-radius: 8px;
  font-weight: 800;
  font-size: 0.9rem;
  background: ${(props) =>
    props.$level === "out"
      ? "#fef2f2"
      : props.$level === "warn"
        ? "#fffbeb"
        : theme.canvas};
  border: 1px solid
    ${(props) =>
      props.$level === "out"
        ? "#fecaca"
        : props.$level === "warn"
          ? "#fde68a"
          : theme.border};
  color: ${(props) =>
    props.$level === "out"
      ? "#b91c1c"
      : props.$level === "warn"
        ? "#92400e"
        : theme.ink700};
`;

// Memoized so a keystroke in the guest form (or the 1s hold-countdown tick) never re-renders the room
// grids. Re-renders only when its own item / busy / stable callbacks change.
// One note per distinct message, not per room: three rooms whose slots all end at 17:00 is one thing
// the front office needs to know, and three identical amber boxes is how a note stops being read.
const transitNotesFor = (item) => {
  const picked = item.selectedRoomIds.length
    ? item.rooms.filter((room) => item.selectedRoomIds.includes(room.id))
    : [];
  return [...new Set(picked.map((room) => room.transit_note).filter(Boolean))];
};

/* Extra beds, per ROOM and per night.
 *
 * It started life as one map for the whole cart item, which cannot express the ordinary case: three
 * Pool Side rooms where two want two beds for the whole stay and the third wants one bed on the first
 * night only. Beds belong to a room, so the picker is keyed by room.
 *
 * Only ticked rooms appear — a bed on a room the operator has not chosen has nowhere to go, and the
 * server refuses it.
 *
 * Collapsed by default: most bookings have no extra bed, and an open grid of steppers for every room
 * would push the confirm button off the card for the common case.
 */
const ExtraBedSection = memo((props) => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const info = props.info;
  const dates = props.stayDates || [];
  const perRoom = info?.per_room || {};
  const selected = props.selectedRoomIds || [];
  const rooms = (props.rooms || []).filter((room) =>
    selected.includes(room.id),
  );

  if (!info || dates.length === 0) return null;

  const cap = info.max_per_room_per_night || 3;
  const free = info.free_per_night || {};
  const otherCarts = info.cart_demand_per_night || {};
  const bedNights = info.bed_nights || 0;

  const qty = (roomId, date) => Number(perRoom[roomId]?.[date] || 0);

  // What this card has already spoken for on a date, excluding the room being adjusted — so a
  // stepper never blocks its own current value, but two rooms cannot both claim the last bed.
  const claimedElsewhere = (roomId, date) =>
    rooms.reduce(
      (sum, room) => (room.id === roomId ? sum : sum + qty(room.id, date)),
      0,
    );

  const maxOn = (roomId, date) => {
    const stock =
      Number(free[date] ?? 0) -
      Number(otherCarts[date] ?? 0) -
      claimedElsewhere(roomId, date);
    return Math.max(0, Math.min(cap, stock));
  };

  const emit = (next) => {
    // Drop rooms that ended up empty so the stored map stays the truth.
    const cleaned = {};
    Object.entries(next).forEach(([roomId, nights]) => {
      const kept = {};
      Object.entries(nights).forEach(([date, value]) => {
        if (Number(value) > 0) kept[date] = Number(value);
      });
      if (Object.keys(kept).length) cleaned[roomId] = kept;
    });
    props.onChange(cleaned);
  };

  const setNight = (roomId, date, value) =>
    emit({
      ...perRoom,
      [roomId]: { ...(perRoom[roomId] || {}), [date]: value },
    });

  const setAllNights = (roomId, value) => {
    const nights = {};
    if (value > 0) {
      dates.forEach((date) => {
        if (maxOn(roomId, date) >= value) nights[date] = value;
      });
    }
    emit({ ...perRoom, [roomId]: nights });
  };

  // Convenience for the common "all rooms the same" case, without giving up per-room control.
  const copyFirstToAll = () => {
    const first = rooms[0];
    if (!first) return;
    const template = perRoom[first.id] || {};
    const next = {};
    rooms.forEach((room) => {
      const nights = {};
      Object.entries(template).forEach(([date, value]) => {
        const allowed = Math.min(Number(value), maxOn(room.id, date) || 0);
        if (allowed > 0) nights[date] = allowed;
      });
      next[room.id] = nights;
    });
    emit(next);
  };

  const roomTotal = (roomId) =>
    dates.reduce((sum, date) => sum + qty(roomId, date), 0);

  return (
    <BedPanel>
      <BedHead type="button" onClick={() => setOpen(!open)}>
        <BedHeadLeft>
          <BedDouble size={14} />
          {t("mdb_extra_bed")}
        </BedHeadLeft>
        <BedHeadRight>
          {bedNights > 0
            ? t("mdb_extra_bed_total")
                .replace("%{beds}", bedNights)
                .replace("%{total}", rupiah(info.total_rupiah))
            : t("mdb_extra_bed_none")}
          {open ? (
            <ChevronUp size={13} style={{ marginLeft: "0.35rem" }} />
          ) : (
            <ChevronDown size={13} style={{ marginLeft: "0.35rem" }} />
          )}
        </BedHeadRight>
      </BedHead>

      {open ? (
        <BedBody>
          <BedHint>
            {t("mdb_extra_bed_hint")}{" "}
            {t("mdb_extra_bed_per_night").replace(
              "%{price}",
              rupiah(info.unit_price_rupiah),
            )}
          </BedHint>

          {rooms.length === 0 ? (
            <BedEmpty>{t("mdb_extra_bed_pick_rooms")}</BedEmpty>
          ) : (
            <>
              {rooms.length > 1 ? (
                <BedQuick>
                  <BedQuickButton
                    type="button"
                    disabled={props.busy || roomTotal(rooms[0].id) === 0}
                    onClick={copyFirstToAll}
                  >
                    {t("mdb_extra_bed_copy_first")}
                  </BedQuickButton>
                </BedQuick>
              ) : null}

              {rooms.map((room) => (
                <BedRoomBlock key={room.id}>
                  <BedRoomHead>
                    <BedRoomCode>
                      {t("mdb_extra_bed_room").replace("%{code}", room.code)}
                    </BedRoomCode>
                    <BedRoomSum>
                      {roomTotal(room.id) > 0
                        ? t("mdb_extra_bed_total")
                            .replace("%{beds}", roomTotal(room.id))
                            .replace(
                              "%{total}",
                              rupiah(
                                roomTotal(room.id) * info.unit_price_rupiah,
                              ),
                            )
                        : t("mdb_extra_bed_none")}
                    </BedRoomSum>
                  </BedRoomHead>

                  <BedQuick>
                    {Array.from({ length: cap }, (_, i) => i + 1).map(
                      (value) => (
                        <BedQuickButton
                          key={value}
                          type="button"
                          disabled={
                            props.busy ||
                            dates.some((d) => maxOn(room.id, d) < value)
                          }
                          onClick={() => setAllNights(room.id, value)}
                        >
                          {t("mdb_extra_bed_all")}: {value}
                        </BedQuickButton>
                      ),
                    )}
                    <BedQuickButton
                      type="button"
                      disabled={props.busy || roomTotal(room.id) === 0}
                      onClick={() => setAllNights(room.id, 0)}
                    >
                      {t("mdb_extra_bed_clear")}
                    </BedQuickButton>
                  </BedQuick>

                  {dates.map((date) => {
                    const value = qty(room.id, date);
                    const max = maxOn(room.id, date);
                    return (
                      <BedRow key={date}>
                        <span>
                          <BedDate>{formatDate(date)}</BedDate>{" "}
                          <BedStock $out={max === 0}>
                            {max === 0
                              ? t("mdb_extra_bed_full")
                              : t("mdb_extra_bed_left").replace("%{n}", max)}
                          </BedStock>
                        </span>
                        <Stepper>
                          <StepButton
                            type="button"
                            aria-label={`-${room.code}-${date}`}
                            disabled={props.busy || value <= 0}
                            onClick={() => setNight(room.id, date, value - 1)}
                          >
                            −
                          </StepButton>
                          <StepValue>{value}</StepValue>
                          <StepButton
                            type="button"
                            aria-label={`+${room.code}-${date}`}
                            disabled={props.busy || value >= max}
                            onClick={() => setNight(room.id, date, value + 1)}
                          >
                            +
                          </StepButton>
                        </Stepper>
                      </BedRow>
                    );
                  })}
                </BedRoomBlock>
              ))}

              {bedNights > 0 ? (
                <BedTotal>
                  {t("mdb_extra_bed_total")
                    .replace("%{beds}", bedNights)
                    .replace("%{total}", rupiah(info.total_rupiah))}
                </BedTotal>
              ) : null}
            </>
          )}
        </BedBody>
      ) : null}
    </BedPanel>
  );
});
ExtraBedSection.displayName = "ExtraBedSection";

const CartItemCard = memo((props) => {
  const { t } = useT();
  const item = props.item;
  const confirmed = item.status === "confirmed";
  return (
    <ItemCard $confirmed={confirmed}>
      <ItemHead>
        <ItemName>
          <BedDouble size={16} /> {item.label} · {item.quantity}{" "}
          {t("mdb_rooms_unit")}
        </ItemName>
        <ItemHeadRight>
          <Badge $confirmed={confirmed}>
            {confirmed ? (
              <>
                <Lock size={11} /> {t("mdb_status_confirmed")}
              </>
            ) : (
              t("mdb_status_draft")
            )}
          </Badge>
          <IconButton type="button" onClick={() => props.onRemove(item)}>
            <Trash2 size={13} /> {t("mdb_remove")}
          </IconButton>
        </ItemHeadRight>
      </ItemHead>

      {confirmed ? (
        <RateLine>
          {t("mdb_selected_rooms")}:{" "}
          <strong>
            {item.rooms
              .filter((room) => item.selectedRoomIds.includes(room.id))
              .map((room) => room.code)
              .join(", ")}
          </strong>
          {item.rateInfo ? (
            <> · {item.rateInfo.total_all_rooms_formatted}</>
          ) : null}
          {item.rooms.some(
            (room) => item.selectedRoomIds.includes(room.id) && room.blocked,
          ) ? (
            <>
              {" "}
              <BlockedBadge>{t("mdb_blocked_badge")}</BlockedBadge>
            </>
          ) : null}
          {item.rooms.some(
            (room) =>
              item.selectedRoomIds.includes(room.id) && room.transit_overlap,
          ) ? (
            <>
              {" "}
              <TransitBadge>{t("mdb_transit_badge")}</TransitBadge>
            </>
          ) : null}
        </RateLine>
      ) : (
        <>
          {item.rooms.length === 0 ? (
            <Muted>{t("mdb_no_rooms")}</Muted>
          ) : (
            <RoomGrid>
              {item.rooms.map((room) => {
                const selected = item.selectedRoomIds.includes(room.id);
                const blockedTitle = room.blocked_all
                  ? t("mdb_blocked_all")
                  : `${t("mdb_blocked_partial")}: ${(room.blocked_dates || []).join(", ")}`;
                const cellTitle = room.blocked
                  ? blockedTitle
                  : room.transit_note || undefined;
                return (
                  <RoomCell
                    key={room.id}
                    type="button"
                    $selected={selected}
                    $blocked={room.blocked}
                    aria-pressed={selected}
                    title={cellTitle}
                    onClick={() => props.onToggleRoom(item.sessionId, room.id)}
                  >
                    <RoomTick $on={selected}>
                      {selected ? <Check size={12} /> : null}
                    </RoomTick>
                    {room.code}
                    {room.blocked ? (
                      <BlockedBadge>{t("mdb_blocked_badge")}</BlockedBadge>
                    ) : null}
                    {room.transit_overlap ? (
                      <TransitBadge>{t("mdb_transit_badge")}</TransitBadge>
                    ) : null}
                  </RoomCell>
                );
              })}
            </RoomGrid>
          )}
          {item.selectedRoomIds.some(
            (id) => item.rooms.find((room) => room.id === id)?.blocked,
          ) ? (
            <BlockedNote>
              <AlertTriangle size={14} /> {t("mdb_blocked_pick_warning")}
            </BlockedNote>
          ) : null}
          {transitNotesFor(item).map((note) => (
            <TransitNote key={note}>
              <Clock size={14} /> {note}
            </TransitNote>
          ))}
          {item.rateInfo ? (
            <RateLine>
              {item.rateInfo.number_of_nights} {t("nights")} ·{" "}
              {item.rateInfo.total_per_room_formatted} / {t("mdb_per_room")} ·{" "}
              <strong>{item.rateInfo.total_all_rooms_formatted}</strong>
            </RateLine>
          ) : null}
          <ConfirmRow>
            <Button
              type="button"
              onClick={() => props.onConfirm(item)}
              disabled={
                props.busy || item.selectedRoomIds.length !== item.quantity
              }
            >
              <Lock size={14} /> {t("mdb_confirm_rooms")} (
              {item.selectedRoomIds.length}/{item.quantity})
            </Button>
          </ConfirmRow>
          {item.error ? (
            <ItemError>
              <AlertTriangle size={14} /> {item.error}
            </ItemError>
          ) : null}
        </>
      )}

      {/* Outside the draft/confirmed branch: the beds can be adjusted right up to submit, and the
          map is only turned into rentals at finalize. Transit stays have no nights — their bed is
          the drawer's flat day-use charge. */}
      {!item.transit ? (
        <ExtraBedSection
          info={item.extraBed}
          stayDates={item.stayDates}
          rooms={item.rooms}
          selectedRoomIds={item.selectedRoomIds}
          busy={props.busy}
          onChange={(perRoom) => props.onExtraBeds(item.sessionId, perRoom)}
        />
      ) : null}
    </ItemCard>
  );
});
CartItemCard.displayName = "CartItemCard";

// Memoized guest form: typing here re-renders only this small form (its own local <input>s), never the
// cart/room grids. Lifts each field up through a stable onChange patch.
const GuestForm = memo((props) => {
  const { t } = useT();
  return (
    <Row>
      <GuestField>
        {t("full_name")} *
        <TextInput
          value={props.guest.name}
          onChange={(event) => props.onChange({ name: event.target.value })}
        />
      </GuestField>
      <GuestField>
        {t("phone")}
        <PhoneField
          value={props.guest.phone}
          onChange={(value) => props.onChange({ phone: value })}
        />
      </GuestField>
      <GuestField>
        {t("email")}
        <TextInput
          value={props.guest.email}
          inputMode="email"
          onChange={(event) => props.onChange({ email: event.target.value })}
        />
      </GuestField>
    </Row>
  );
});
GuestForm.displayName = "GuestForm";

const Dock = styled.aside`
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  z-index: 900;
  width: 320px;
  max-width: calc(100vw - 2rem);
  max-height: 50vh;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 12px;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.16);
  padding: 0.6rem 0.75rem;
`;

const DockSection = styled.div`
  &:not(:last-child) {
    margin-bottom: 0.6rem;
    padding-bottom: 0.6rem;
    border-bottom: 1px solid ${theme.border};
  }
`;

const DockTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${theme.muted};
  margin-bottom: 0.4rem;
`;

const DockCurrent = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.6rem;
  border-radius: 8px;
  background: ${theme.goldSoft};
  color: ${theme.goldDark};
  font-weight: 800;
  font-variant-numeric: tabular-nums;
`;

const DockRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.35rem 0;
`;

const DockMain = styled.div`
  font-size: 0.88rem;
  font-weight: 700;
  color: ${theme.ink};
`;

const DockSub = styled.div`
  font-size: 0.76rem;
  color: ${theme.muted};
  font-variant-numeric: tabular-nums;
`;

const MiniButton = styled.button`
  flex: 0 0 auto;
  padding: 0.28rem 0.6rem;
  border: 1px solid ${theme.gold};
  border-radius: 8px;
  background: ${theme.goldSoft};
  color: ${theme.goldDark};
  font-size: 0.78rem;
  font-weight: 800;
  cursor: pointer;
  &:hover {
    background: ${theme.gold};
    color: #ffffff;
  }
`;

const DockHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
`;

const ToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.15rem 0.4rem;
  border: 1px solid ${theme.border};
  border-radius: 999px;
  background: #ffffff;
  color: ${theme.ink700};
  font-size: 0.72rem;
  font-weight: 800;
  cursor: pointer;
  &:hover {
    border-color: ${theme.gold};
  }
`;

const RecentCard = styled.div`
  border: 1px solid ${theme.border};
  border-left: 3px solid ${theme.gold};
  border-radius: 8px;
  padding: 0.45rem 0.55rem;
  &:not(:last-child) {
    margin-bottom: 0.4rem;
  }
`;

const RecentTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const RecentVoucher = styled.span`
  font-size: 0.86rem;
  font-weight: 800;
  color: ${theme.ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RoomsPill = styled.span`
  flex: 0 0 auto;
  padding: 0.05rem 0.4rem;
  border-radius: 999px;
  background: ${theme.canvas};
  border: 1px solid ${theme.border};
  font-size: 0.7rem;
  font-weight: 800;
  color: ${theme.ink700};
`;

const RecentGuest = styled.div`
  font-size: 0.78rem;
  color: ${theme.ink700};
  margin-top: 0.15rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RecentTime = styled.div`
  font-size: 0.72rem;
  color: ${theme.muted};
  font-variant-numeric: tabular-nums;
  margin-top: 0.1rem;
`;

const RecentOpen = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.35rem;
  padding: 0.2rem 0.55rem;
  border: 0;
  border-radius: 7px;
  background: ${theme.gold};
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 800;
  cursor: pointer;
  &:hover {
    background: ${theme.goldDark};
  }
`;

// Bottom-right dock: the live hold countdown (always visible while working), any OTHER in-progress cart
// that can be resumed after a mistaken refresh, and recently-created bookings to reopen.
// The dock's one-line label has to distinguish a held HOUR SLOT from a night range, or a transit cart
// reads as "30 Jul → 30 Jul" — an impossible nightly stay.
const dockTransitWindow = (entry) => {
  const item = (entry.items || []).find((row) => row.transit_from);
  return item
    ? `${timeCompact(item.transit_from)} → ${timeCompact(item.transit_to)}`
    : "—";
};

const RecoveryDock = memo((props) => {
  const { t } = useT();
  const [recentOpen, setRecentOpen] = useState(false);
  const others = props.activeCarts.filter(
    (entry) => entry.cart_id !== props.currentCartId,
  );
  const countdown = (iso) => {
    if (!iso) {
      return "";
    }
    const total = Math.max(0, Math.floor((Date.parse(iso) - props.now) / 1000));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
  };
  const hasCurrent = Boolean(props.currentCartId && props.cartExpiresAt);
  if (!hasCurrent && others.length === 0 && props.recentBookings.length === 0) {
    return null;
  }
  return (
    <Dock>
      {hasCurrent ? (
        <DockSection>
          <DockTitle>{t("mdb_dock_current")}</DockTitle>
          <DockCurrent>
            <Timer size={13} /> {countdown(props.cartExpiresAt)}
          </DockCurrent>
        </DockSection>
      ) : null}
      {others.length ? (
        <DockSection>
          <DockTitle>{t("mdb_dock_resume")}</DockTitle>
          {others.map((entry) => (
            <DockRow key={entry.cart_id}>
              <div>
                <DockMain>
                  {entry.stay_kind === "transit"
                    ? `${entry.check_in_date} · ${dockTransitWindow(entry)}`
                    : `${entry.check_in_date} → ${entry.check_out_date}`}
                </DockMain>
                <DockSub>
                  {entry.stay_kind === "transit"
                    ? t("transit")
                    : `${entry.items.length} ${t("mdb_room_type")}`}{" "}
                  · {countdown(entry.expires_at)}
                </DockSub>
              </div>
              <MiniButton type="button" onClick={() => props.onResume(entry)}>
                {t("mdb_resume")}
              </MiniButton>
            </DockRow>
          ))}
        </DockSection>
      ) : null}
      {props.recentBookings.length ? (
        <DockSection>
          <DockHead>
            <DockTitle>{t("mdb_dock_recent")}</DockTitle>
            {props.recentBookings.length > 1 ? (
              <ToggleBtn
                type="button"
                onClick={() => setRecentOpen((open) => !open)}
                aria-expanded={recentOpen}
              >
                {recentOpen ? (
                  <>
                    <ChevronUp size={13} /> {t("mdb_recent_less")}
                  </>
                ) : (
                  <>
                    <ChevronDown size={13} />{" "}
                    {t("mdb_recent_more").replace(
                      "%{n}",
                      Math.min(5, props.recentBookings.length),
                    )}
                  </>
                )}
              </ToggleBtn>
            ) : null}
          </DockHead>
          {props.recentBookings.slice(0, recentOpen ? 5 : 1).map((booking) => (
            <RecentCard key={booking.id}>
              <RecentTop>
                <RecentVoucher>
                  {booking.voucher_code || booking.guest_name || "—"}
                </RecentVoucher>
                <RoomsPill>
                  {booking.rooms_count} {t("mdb_rooms_unit")}
                </RoomsPill>
              </RecentTop>
              {booking.guest_name ? (
                <RecentGuest>{booking.guest_name}</RecentGuest>
              ) : null}
              {booking.created_at_label ? (
                <RecentTime>{booking.created_at_label}</RecentTime>
              ) : null}
              {booking.line_id ? (
                <RecentOpen
                  type="button"
                  onClick={() => props.onOpenBooking(booking.line_id)}
                >
                  <ExternalLink size={12} /> {t("mdb_open")}
                </RecentOpen>
              ) : null}
            </RecentCard>
          ))}
        </DockSection>
      ) : null}
    </Dock>
  );
});
RecoveryDock.displayName = "RecoveryDock";

const ModeBtn = styled.button`
  background: ${(props) => (props.$active ? "#3f3c36" : "#ffffff")};
  border: 1px solid ${(props) => (props.$active ? "#3f3c36" : "#ddd8cc")};
  border-radius: 8px;
  color: ${(props) => (props.$active ? "#ffffff" : "#3f3c36")};
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 700;
  padding: 8px 16px;
`;

const TransitSummary = styled.div`
  background: ${(props) => (props.$pending ? "#fffbeb" : "#faf9ff")};
  border: 1px solid ${(props) => (props.$pending ? "#fde68a" : "#e6e0fb")};
  border-left: 3px solid ${(props) => (props.$pending ? "#b45309" : "#6d28d9")};
  border-radius: 8px;
  margin-bottom: 8px;
  padding: 10px 12px;
`;

const TransitSummaryHead = styled.div`
  align-items: center;
  color: #4c1d95;
  display: flex;
  font-size: 0.9rem;
  font-weight: 800;
  gap: 6px;
`;

const TransitPending = styled.div`
  align-items: center;
  color: #92400e;
  display: flex;
  font-size: 0.8rem;
  font-weight: 700;
  gap: 5px;
  margin-top: 6px;
`;

// A resumed cart supplies from/to directly; a freshly locked one supplies ISO timestamps. Both render
// the same, and a missing window degrades to a dash rather than to "undefined → undefined".
const transitWindowLabel = (item, t) => {
  const from = item.transit?.from || item.transit?.startsAt;
  const to = item.transit?.to || item.transit?.endsAt;
  if (!from || !to) return "—";

  return `${timeWithDayPart(from, t)} → ${timeWithDayPart(to, t)}`;
};

const DirectBookingPage = (props) => {
  // Overnight and transit are two different products sold from one page. The toggle keeps the
  // nightly wizard untouched and swaps in the transit picker, rather than threading a stay kind
  // through every step of a flow that has no concept of hours.
  const [stayKind, setStayKind] = useState("overnight");
  const { t, lang } = useT();
  const initialDate = props.date || wibTodayIso();
  const [checkIn, setCheckIn] = useState(initialDate);
  const [checkOut, setCheckOut] = useState(() => shiftIso(initialDate, 1));
  const [cart, setCart] = useState(null);
  const [items, setItems] = useState([]);
  const [addType, setAddType] = useState("");
  const [addQty, setAddQty] = useState(1);
  const [guest, setGuest] = useState({ name: "", phone: "", email: "" });
  const [dialog, setDialog] = useState(null);
  const [readAloud, setReadAloud] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cartExpiresAt, setCartExpiresAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [activeCarts, setActiveCarts] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);

  // Tick every second whenever there's anything to count down (current or resumable carts). Carts are
  // NOT auto-cancelled on leave/refresh — the 15-min TTL + revert job is the abandoned-cart backstop, so
  // a mistaken refresh can be resumed from the dock instead of losing the held rooms.
  useEffect(() => {
    if (!cartExpiresAt && activeCarts.length === 0) {
      return undefined;
    }
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [cartExpiresAt, activeCarts.length]);

  const loadRecovery = useCallback(async () => {
    const [cartsRes, recentRes] = await Promise.all([
      apiFetch("/frontdesk_v2/direct_booking/active_carts").catch(() => null),
      apiFetch("/frontdesk_v2/direct_booking/recent").catch(() => null),
    ]);
    if (cartsRes?.data) {
      setActiveCarts(cartsRes.data.carts || []);
    }
    if (recentRes?.data) {
      setRecentBookings(recentRes.data.bookings || []);
    }
  }, []);

  useEffect(() => {
    loadRecovery();
  }, [loadRecovery]);

  // Resuming has to restore the MODE too. A transit cart holds an hourly slot that was already priced
  // and locked; dropping it into the nightly wizard offers a room picker over a zero-night range and
  // quotes Rp 0 for a stay the guest is about to pay 80k for.
  const resumeCart = useCallback((entry) => {
    const isTransit = entry.stay_kind === "transit";
    setStayKind(isTransit ? "transit" : "overnight");
    setCheckIn(entry.check_in_date);
    // A transit cart is one day; leaving checkOut at the resumed date would render an invalid nightly
    // range if the FO then switched back to Menginap.
    setCheckOut(
      isTransit ? shiftIso(entry.check_in_date, 1) : entry.check_out_date,
    );
    setCart({ cartId: entry.cart_id, roomTypes: entry.room_types || [] });
    setItems(
      (entry.items || []).map((item) => ({
        sessionId: item.session_id,
        roomTypeId: item.room_type_id,
        label: item.room_type_label,
        quantity: item.number_of_rooms,
        status: item.status === "hold_confirmed" ? "confirmed" : "draft",
        rooms: item.rooms || [],
        selectedRoomIds: item.selected_room_ids || [],
        rateInfo: item.rate_info,
        stayDates: item.stay_dates || [],
        extraBed: item.extra_bed,
        error: "",
        transit:
          item.stay_kind === "transit"
            ? {
                roomCode: item.transit_room_code,
                startsAt: item.transit_starts_at,
                endsAt: item.transit_ends_at,
                from: item.transit_from,
                to: item.transit_to,
                durationHours: item.transit_duration_hours,
                crossesMidnight: item.transit_crosses_midnight,
              }
            : undefined,
      })),
    );
    setCartExpiresAt(entry.expires_at || null);
    setError("");
  }, []);

  const openBooking = useCallback(
    (lineId) => router.visit(`/frontdesk_v2/rooms_grid?drawer=${lineId}`),
    [],
  );

  const patchItem = useCallback(
    (sessionId, patch) =>
      setItems((prev) =>
        prev.map((item) =>
          item.sessionId === sessionId ? { ...item, ...patch } : item,
        ),
      ),
    [],
  );

  // Stable so the memoized CartItemCards never re-render on a guest keystroke / countdown tick.
  const onGuestChange = useCallback(
    (patch) => setGuest((prev) => ({ ...prev, ...patch })),
    [],
  );

  const onToggleRoom = useCallback(
    (sessionId, roomId) =>
      setItems((prev) =>
        prev.map((item) => {
          if (item.sessionId !== sessionId) {
            return item;
          }
          if (item.selectedRoomIds.includes(roomId)) {
            return {
              ...item,
              selectedRoomIds: item.selectedRoomIds.filter(
                (id) => id !== roomId,
              ),
            };
          }
          if (item.selectedRoomIds.length < item.quantity) {
            return {
              ...item,
              selectedRoomIds: [...item.selectedRoomIds, roomId],
            };
          }
          return item;
        }),
      ),
    [],
  );

  const startCart = async () => {
    setBusy(true);
    setError("");
    const res = await apiFetch("/frontdesk_v2/direct_booking/start", {
      method: "POST",
      body: JSON.stringify({
        check_in_date: checkIn,
        check_out_date: checkOut,
      }),
    }).catch(() => null);
    setBusy(false);
    if (!res?.data) {
      setError(res?.message || t("mdb_error_generic"));
      return;
    }
    setItems([]);
    setAddType("");
    setCartExpiresAt(null);
    setCart({ cartId: res.data.cart_id, roomTypes: res.data.room_types || [] });
  };

  const addItem = async () => {
    if (!cart || !addType) return;
    setBusy(true);
    setError("");
    const res = await apiFetch("/frontdesk_v2/direct_booking/items", {
      method: "POST",
      body: JSON.stringify({
        cart_id: cart.cartId,
        room_type_id: addType,
        number_of_rooms: Math.max(1, Number(addQty) || 1),
        check_in_date: checkIn,
        check_out_date: checkOut,
      }),
    }).catch(() => null);
    setBusy(false);
    if (!res?.data) {
      setError(res?.message || t("mdb_error_generic"));
      return;
    }
    const data = res.data;
    setItems((prev) => [
      ...prev.filter((item) => item.sessionId !== data.session_id),
      {
        sessionId: data.session_id,
        roomTypeId: data.room_type_id,
        label: data.room_type_label,
        quantity: data.number_of_rooms,
        status: "draft",
        rooms: data.rooms || [],
        selectedRoomIds: [],
        rateInfo: data.rate_info,
        stayDates: data.stay_dates || [],
        extraBed: data.extra_bed,
        error: "",
      },
    ]);
    setCartExpiresAt(data.cart_expires_at || null);
    setAddType("");
    setAddQty(1);
  };

  // Optimistic on the way out and authoritative on the way back: the server re-checks the cap and the
  // per-date stock, so a map it trims is the one that sticks.
  const setExtraBeds = useCallback(
    async (sessionId, perRoom) => {
      patchItem(sessionId, { error: "" });
      const res = await apiFetch(
        `/frontdesk_v2/direct_booking/items/${sessionId}/extra_beds`,
        { method: "POST", body: JSON.stringify({ per_room: perRoom }) },
      ).catch(() => null);
      if (!res?.data) {
        patchItem(sessionId, {
          error: res?.message || t("mdb_error_generic"),
        });
        return;
      }
      setCartExpiresAt(res.data.cart_expires_at || null);
      patchItem(sessionId, { extraBed: res.data.extra_bed, error: "" });
    },
    [patchItem, t],
  );

  const doConfirmItem = useCallback(
    async (item) => {
      setBusy(true);
      patchItem(item.sessionId, { error: "" });
      const res = await apiFetch(
        `/frontdesk_v2/direct_booking/items/${item.sessionId}/confirm`,
        {
          method: "POST",
          body: JSON.stringify({ room_ids: item.selectedRoomIds }),
        },
      ).catch(() => null);
      setBusy(false);
      if (!res?.data) {
        patchItem(item.sessionId, {
          error: res?.message || t("mdb_error_generic"),
        });
        return;
      }
      setCartExpiresAt(res.data.cart_expires_at || null);
      patchItem(item.sessionId, { status: "confirmed", error: "" });
    },
    [patchItem, t],
  );

  const askConfirmItem = useCallback(
    (item) => {
      setDialog({
        title: t("mdb_confirm_rooms_title"),
        body: t("mdb_confirm_rooms_body")
          .replace("%{count}", item.quantity)
          .replace("%{type}", item.label),
        note: t("mdb_confirm_rooms_note"),
        confirmLabel: t("mdb_confirm_rooms_ok"),
        onConfirm: () => {
          setDialog(null);
          doConfirmItem(item);
        },
        onCancel: () => setDialog(null),
      });
    },
    [doConfirmItem, t],
  );

  const removeItem = useCallback(
    async (item) => {
      setBusy(true);
      const res = await apiFetch(
        `/frontdesk_v2/direct_booking/items/${item.sessionId}`,
        { method: "DELETE" },
      ).catch(() => null);
      setBusy(false);
      if (!res?.data && !res?.message) {
        setError(t("mdb_error_generic"));
        return;
      }
      setCartExpiresAt(res.data?.cart_expires_at || null);
      setItems((prev) =>
        prev.filter((entry) => entry.sessionId !== item.sessionId),
      );
    },
    [t],
  );

  // A slow tick that always runs: the cart countdown's 1 s interval only exists while a cart does, so
  // without this the late-night banner never appears for an FO who opened the page before midnight.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const confirmedItems = items.filter((item) => item.status === "confirmed");

  /* The date this booking is actually FOR.
   *
   * TransitPicker keeps its own `date` state and posts it as `transit_date` without lifting it, so
   * once a transit slot is held `checkIn` is still whatever the page loaded with — today, for a
   * booking the operator made for next week. The held slot's start is the authoritative answer, and
   * everything that reports the booking BACK must read it: the read-aloud script, the post-submit
   * redirect, and the midnight warning. (A resumed cart already arrives with the right check_in_date,
   * and carries `from`/`to` rather than `startsAt`, so it falls through to `checkIn` correctly.)
   */
  const heldTransitStartsAt = confirmedItems.find(
    (item) => item.transit?.startsAt,
  )?.transit?.startsAt;
  // Slice rather than parse: `starts_at` is a Rails iso8601 string, and building a Date would
  // reinterpret it in the browser's zone and can land on the previous day.
  const bookedDate = heldTransitStartsAt
    ? heldTransitStartsAt.slice(0, 10)
    : checkIn;

  // Graduated by what is actually at stake, not by "does a cart object exist". A cart is created the
  // moment room types are loaded, so locking on that alone stopped an FO fixing a date they had only
  // just noticed was wrong.
  //   none    — nothing held, edit freely
  //   items   — types added but no room locked: removing them is cheap and local
  //   locked  — a room is held in eZee, so the dates belong to that hold now
  const dateLock =
    confirmedItems.length > 0 ? "locked" : items.length > 0 ? "items" : "none";
  const datesEditable = dateLock === "none";
  const allConfirmed =
    items.length > 0 && confirmedItems.length === items.length;
  const roomsRupiah = confirmedItems.reduce(
    (sum, item) => sum + Number(item.rateInfo?.total_all_rooms_rupiah || 0),
    0,
  );
  // Extra beds are part of what the guest pays (ComputeAutoAmountService sweeps them into the same
  // payment), so they belong in the figure the front desk reads aloud. Leaving them out quoted a
  // total short by the whole bed charge.
  const extraBedRupiah = confirmedItems.reduce(
    (sum, item) => sum + Number(item.extraBed?.total_rupiah || 0),
    0,
  );
  // Present only while the walk-in room tax is on (RoomTax), so the total is unchanged otherwise.
  // The rooms are taxed, never the extra beds.
  const roomTaxRupiah = confirmedItems.reduce(
    (sum, item) => sum + Number(item.rateInfo?.room_tax_all_rooms_rupiah || 0),
    0,
  );
  const totalRupiah = roomsRupiah + roomTaxRupiah + extraBedRupiah;
  const totalRooms = confirmedItems.reduce(
    (sum, item) => sum + item.quantity,
    0,
  );
  const totalBedNights = confirmedItems.reduce(
    (sum, item) => sum + Number(item.extraBed?.bed_nights || 0),
    0,
  );
  const expiresMs = cartExpiresAt ? Date.parse(cartExpiresAt) : null;
  const secondsLeft =
    expiresMs != null
      ? Math.max(0, Math.floor((expiresMs - now) / 1000))
      : null;
  const timedOut = secondsLeft === 0;
  const holdLevel = timedOut ? "out" : secondsLeft <= 120 ? "warn" : "ok";
  const fmtCountdown = (total) =>
    `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
  const canSubmit =
    allConfirmed && guest.name.trim().length > 0 && !busy && !timedOut;

  const doSubmit = async () => {
    setBusy(true);
    setError("");
    const res = await apiFetch("/frontdesk_v2/direct_booking/submit", {
      method: "POST",
      body: JSON.stringify({
        cart_id: cart.cartId,
        guest_name: guest.name,
        guest_phone: guest.phone,
        guest_email: guest.email,
      }),
    }).catch(() => null);
    if (!res?.data) {
      setBusy(false);
      setError(res?.message || t("mdb_error_generic"));
      return;
    }
    const target = res.data.line_id
      ? `/frontdesk_v2/rooms_grid?date=${bookedDate}&drawer=${res.data.line_id}`
      : `/frontdesk_v2/rooms_grid?date=${bookedDate}`;
    router.visit(target);
  };

  // Selected rooms (across all confirmed types) that are intentionally blocked — drives the extra
  // explain-everything confirmation before creating the booking.
  const blockedSelections = confirmedItems.flatMap((item) =>
    item.rooms
      .filter((room) => item.selectedRoomIds.includes(room.id) && room.blocked)
      .map((room) => ({
        code: room.code,
        nights: room.blocked_all
          ? t("mdb_blocked_all")
          : (room.blocked_dates || []).join(", "),
      })),
  );

  // The room is still bookable — the day-use guest leaves before this guest arrives, normally. The
  // read-aloud is where the front office finds out it is not normal today, while the guest is still
  // standing there and a different room is still free.
  const transitOverlapNotes = [
    ...new Set(
      confirmedItems.flatMap((item) =>
        item.rooms
          .filter(
            (room) =>
              item.selectedRoomIds.includes(room.id) && room.transit_overlap,
          )
          .map((room) => `${room.code}: ${room.transit_note}`),
      ),
    ),
  ];

  // What the FO reads to the guest. Dates carry their day name, because "Jum, 15 Agu" is something a
  // guest can contradict and "2026-08-15" is not.
  const readAloudPoints = () => {
    const rooms = confirmedItems
      .map((item) => `${item.label} × ${item.quantity}`)
      .join(", ");

    const points = [
      { label: t("read_aloud_guest"), value: guest.name.trim() || "—" },
      { label: t("read_aloud_rooms"), value: rooms || "—" },
      {
        label: t("read_aloud_check_in"),
        value: formatDate(bookedDate, lang, false),
        emphasis: true,
      },
    ];

    if (stayKind === "transit") {
      // Read the actual hours and room back, not just the word "transit" — the hour window IS the
      // booking here, and a slot that crosses midnight is the one most worth confirming aloud.
      confirmedItems.forEach((item) => {
        points.push({
          label: t("read_aloud_stay"),
          value: `${item.transit?.roomCode || item.label} · ${transitWindowLabel(item, t)}${
            item.transit?.crossesMidnight
              ? ` (${t("read_aloud_crosses_midnight")})`
              : ""
          }`,
          emphasis: true,
        });
      });
    } else {
      points.push({
        label: t("read_aloud_check_out"),
        value: formatDate(checkOut, lang, false),
        emphasis: true,
      });
      points.push({
        label: t("read_aloud_nights"),
        value: t("read_aloud_nights_value").replace("%{nights}", nights),
      });
    }

    if (extraBedRupiah > 0 || roomTaxRupiah > 0) {
      points.push({
        label: t("read_aloud_rooms_total"),
        value: rupiah(roomsRupiah),
      });
    }
    if (roomTaxRupiah > 0) {
      points.push({ label: t("pay_room_tax"), value: rupiah(roomTaxRupiah) });
    }
    if (extraBedRupiah > 0) {
      points.push({
        label: t("read_aloud_extra_beds"),
        value: t("read_aloud_extra_beds_value")
          .replace("%{beds}", totalBedNights)
          .replace("%{total}", rupiah(extraBedRupiah)),
      });
    }
    points.push({ label: t("read_aloud_total"), value: rupiah(totalRupiah) });
    if (blockedSelections.length > 0) {
      points.push({
        label: t("read_aloud_blocked"),
        value: blockedSelections.map((sel) => sel.code).join(", "),
      });
    }
    return points;
  };

  // Mirrors the picker's warning so the script the FO reads aloud carries it too. Slot timestamps
  // come back on the confirmed item, so this needs no extra request.
  const pastTransitWarning = () => {
    if (stayKind !== "transit") return null;

    const now = Date.now();
    const slots = confirmedItems
      .map((item) => item.transit)
      .filter((slot) => slot?.endsAt || slot?.startsAt);
    if (slots.length === 0) return null;

    if (slots.some((slot) => Date.parse(slot.endsAt) <= now)) {
      return t("transit_past_ended");
    }
    if (slots.some((slot) => Date.parse(slot.startsAt) <= now)) {
      return t("transit_past_started");
    }
    return null;
  };

  const askSubmit = () => {
    setReadAloud({
      script: t("read_aloud_script"),
      points: readAloudPoints(),
      // Every applicable warning, not the highest-priority one: a 01:00 walk-in booked into a transit
      // slot that already ended needs to hear both, and the old ternary dropped the second silently.
      warnings: [
        lateNightRisk &&
          t("read_aloud_midnight_warning").replace(
            "%{date}",
            formatDate(wibTodayIso(), lang, false),
          ),
        pastTransitWarning(),
        blockedSelections.length > 0 && t("mdb_submit_blocked_note"),
        ...transitOverlapNotes,
      ].filter(Boolean),
      onConfirm: () => {
        setReadAloud(null);
        doSubmit();
      },
      onCancel: () => setReadAloud(null),
    });
  };

  const cancelAll = async () => {
    if (cart) {
      await apiFetch("/frontdesk_v2/direct_booking/cancel", {
        method: "POST",
        body: JSON.stringify({ cart_id: cart.cartId }),
      }).catch(() => null);
    }
    setCart(null);
    setItems([]);
    setGuest({ name: "", phone: "", email: "" });
    setError("");
    setCartExpiresAt(null);
    loadRecovery();
  };

  const availableTypes = (cart?.roomTypes || []).filter(
    (type) => !items.some((item) => item.roomTypeId === type.room_type_id),
  );

  const startMs = Date.parse(checkIn);
  const endMs = Date.parse(checkOut);
  const nights =
    Number.isNaN(startMs) || Number.isNaN(endMs)
      ? 0
      : Math.max(0, Math.round((endMs - startMs) / 86_400_000));

  // The mistake this page keeps producing: a guest walks in at 01:00, the system has already rolled
  // the day, and the booking is dated today when the stay really began yesterday. Flagged from the
  // WIB wall clock, never the browser's own timezone.
  const wibHour = Number(wibTime(new Date(now)).slice(0, 2));
  const lateNight = wibHour < 6;
  const lateNightRisk = lateNight && bookedDate === wibTodayIso();

  return (
    <PageShell>
      <Head title={t("mdb_page_title")} />
      <Wrap>
        {error ? <PageError>{error}</PageError> : null}

        {cart && items.length > 0 && secondsLeft !== null ? (
          <HoldBar $level={holdLevel}>
            <Timer size={15} />
            {/* A transit hold is a row in room_transit_slots, not an eZee inventory hold — naming it
                "eZee" would tell the FO the channel manager is holding stock that it is not. */}
            {timedOut
              ? t(
                  stayKind === "transit"
                    ? "mdb_expired_transit"
                    : "mdb_expired",
                )
              : `${t(
                  stayKind === "transit"
                    ? "mdb_hold_timer_transit"
                    : "mdb_hold_timer",
                )}: ${fmtCountdown(secondsLeft)}`}
          </HoldBar>
        ) : null}

        <Panel>
          <Row style={{ marginBottom: 12 }}>
            <ModeBtn
              type="button"
              $active={stayKind === "overnight"}
              onClick={() => setStayKind("overnight")}
            >
              Menginap
            </ModeBtn>
            <ModeBtn
              type="button"
              $active={stayKind === "transit"}
              onClick={() => setStayKind("transit")}
            >
              Transit (per jam)
            </ModeBtn>
          </Row>

          {stayKind === "transit" ? (
            <TransitPicker
              date={checkIn}
              durations={props.transit_durations}
              // The slot is already held server-side at this point, so the picker hands the cart
              // straight to the shared guest-details + submit tail rather than reloading the page —
              // a reload would drop the cart id and strand a held room until the TTL reaped it.
              onConfirmed={(confirmed) => {
                setError("");
                setCart({ cartId: confirmed.cartId, roomTypes: [] });
                setItems([
                  {
                    sessionId: confirmed.sessionId,
                    roomTypeId: confirmed.roomTypeId,
                    label: confirmed.roomTypeLabel,
                    quantity: 1,
                    status: "confirmed",
                    rooms: [],
                    selectedRoomIds: [confirmed.roomId],
                    rateInfo: confirmed.rateInfo,
                    error: "",
                    transit: {
                      roomCode: confirmed.roomCode,
                      startsAt: confirmed.startsAt,
                      endsAt: confirmed.endsAt,
                      from: (confirmed.startsAt || "").slice(11, 16),
                      to: (confirmed.endsAt || "").slice(11, 16),
                      durationHours: confirmed.durationHours,
                      crossesMidnight: confirmed.crossesMidnight,
                      acknowledged: confirmed.acknowledged,
                    },
                  },
                ]);
                setCartExpiresAt(confirmed.cartExpiresAt || null);
                loadRecovery();
              }}
            />
          ) : (
            <>
              <PanelTitle>{t("mdb_dates_title")}</PanelTitle>
              {lateNight ? (
                <LateNightBar>
                  <AlertTriangle size={16} style={{ flex: "0 0 auto" }} />
                  <span>
                    {t("mdb_late_night_hint").replace(
                      "%{today}",
                      formatDate(wibTodayIso(), lang, false),
                    )}
                  </span>
                  {checkIn === wibTodayIso() && datesEditable ? (
                    <YesterdayBtn
                      type="button"
                      onClick={() => {
                        const yesterday = shiftIso(wibTodayIso(), -1);
                        setCheckIn(yesterday);
                        if (Date.parse(checkOut) <= Date.parse(yesterday)) {
                          setCheckOut(shiftIso(yesterday, 1));
                        }
                      }}
                    >
                      {t("mdb_use_yesterday")}
                    </YesterdayBtn>
                  ) : null}
                </LateNightBar>
              ) : null}
              <Row>
                {/* Locked once rooms are held: the cart carries the nights it was created with, and
                    submit sends only its id. An editable field here would let the form — and the
                    read-aloud script — state a date the booking is not actually for. */}
                <DatePickField
                  label={t("check_in")}
                  value={checkIn}
                  disabled={!datesEditable}
                  onChange={setCheckIn}
                />
                <DatePickField
                  label={t("check_out")}
                  value={checkOut}
                  min={shiftIso(checkIn, 1)}
                  disabled={!datesEditable}
                  onChange={setCheckOut}
                />
                <NightsBadge $invalid={nights <= 0}>
                  <Moon size={15} />
                  {nights} {t("nights")}
                </NightsBadge>
                <Button type="button" onClick={startCart} disabled={busy}>
                  {busy ? <Loader2 size={15} /> : <BedDouble size={15} />}
                  {cart ? t("mdb_reload_types") : t("mdb_load_types")}
                </Button>
                {cart ? (
                  <GhostButton
                    type="button"
                    onClick={cancelAll}
                    disabled={busy}
                  >
                    {t("mdb_reset")}
                  </GhostButton>
                ) : null}
              </Row>
              {datesEditable ? null : (
                <DateLockNote>
                  <Lock size={14} style={{ flex: "0 0 auto" }} />
                  <span>
                    {dateLock === "locked"
                      ? t("mdb_dates_locked_confirmed")
                      : t("mdb_dates_locked_items")}
                  </span>
                </DateLockNote>
              )}
            </>
          )}
        </Panel>

        {/* The transit picker already chose the room type, the room and the hours, so the nightly
            add-a-room-type widget has nothing left to ask — it shows the locked slot instead. */}
        {stayKind === "transit" && items.length > 0 ? (
          <Panel>
            <PanelTitle>{t("mdb_rooms_title")}</PanelTitle>
            {items.map((item) => (
              <TransitSummary
                key={item.sessionId}
                $pending={item.status !== "confirmed"}
              >
                <TransitSummaryHead>
                  <Timer size={15} aria-hidden="true" />
                  {t("transit")} · {t("caption_room")}{" "}
                  {item.transit?.roomCode || "—"} ·{" "}
                  {transitWindowLabel(item, t)}
                </TransitSummaryHead>
                <Muted>
                  {item.label} · {item.transit?.durationHours}{" "}
                  {t("transit_hours")} ·{" "}
                  {item.rateInfo?.total_all_rooms_formatted || "—"}
                  {item.transit?.crossesMidnight
                    ? ` · ${t("transit_crosses_midnight")}`
                    : ""}
                </Muted>
                {/* A transit item can only sit unconfirmed if the lock call failed after the item was
                    added. The picker owns locking, so there is nothing to retry from here — say the
                    slot is not held instead of showing a submit button that can never enable. */}
                {item.status === "confirmed" ? null : (
                  <TransitPending>
                    <AlertTriangle size={13} aria-hidden="true" />
                    {t("mdb_transit_not_locked")}
                  </TransitPending>
                )}
              </TransitSummary>
            ))}
            <Row style={{ marginTop: 10 }}>
              <GhostButton type="button" onClick={cancelAll} disabled={busy}>
                {t("mdb_reset")}
              </GhostButton>
            </Row>
          </Panel>
        ) : null}

        {cart && stayKind !== "transit" ? (
          <Panel>
            <PanelTitle>{t("mdb_rooms_title")}</PanelTitle>
            <Row>
              <Field>
                {t("mdb_room_type")}
                <Select
                  value={addType}
                  onChange={(event) => setAddType(event.target.value)}
                >
                  <option value="">{t("mdb_pick_type")}</option>
                  {availableTypes.map((type) => (
                    <option key={type.room_type_id} value={type.room_type_id}>
                      {type.room_type_label} ({type.min_available})
                    </option>
                  ))}
                </Select>
              </Field>
              <Field>
                {t("mdb_qty")}
                <NumberInput
                  type="number"
                  min="1"
                  value={addQty}
                  // Allow the field to go empty while typing (staff clear it before retyping "2");
                  // only snap back to a valid count on blur.
                  onChange={(event) => {
                    const raw = event.target.value;
                    if (raw === "" || /^\d+$/.test(raw)) {
                      setAddQty(raw);
                    }
                  }}
                  onBlur={() =>
                    setAddQty((prev) => Math.max(1, Number(prev) || 1))
                  }
                />
              </Field>
              <Button
                type="button"
                onClick={addItem}
                disabled={busy || !addType}
              >
                <Plus size={15} /> {t("mdb_add")}
              </Button>
            </Row>

            <div style={{ marginTop: "1rem" }}>
              {items.length === 0 ? (
                <Muted>{t("mdb_no_items")}</Muted>
              ) : (
                items.map((item) => (
                  <CartItemCard
                    key={item.sessionId}
                    item={item}
                    busy={busy}
                    onToggleRoom={onToggleRoom}
                    onConfirm={askConfirmItem}
                    onRemove={removeItem}
                    onExtraBeds={setExtraBeds}
                  />
                ))
              )}
            </div>
          </Panel>
        ) : null}

        {confirmedItems.length > 0 ? (
          <Panel>
            <PanelTitle>{t("mdb_guest_title")}</PanelTitle>
            <GuestForm guest={guest} onChange={onGuestChange} />
          </Panel>
        ) : null}

        {items.length > 0 ? (
          <SummaryBar>
            <Total>
              {totalRooms} {t("mdb_rooms_unit")} · {rupiah(totalRupiah)}
            </Total>
            <Button type="button" onClick={askSubmit} disabled={!canSubmit}>
              <Check size={16} /> {t("mdb_submit")}
            </Button>
          </SummaryBar>
        ) : null}
      </Wrap>

      <RecoveryDock
        currentCartId={cart?.cartId || null}
        cartExpiresAt={cartExpiresAt}
        activeCarts={activeCarts}
        recentBookings={recentBookings}
        now={now}
        onResume={resumeCart}
        onOpenBooking={openBooking}
      />

      {readAloud ? (
        <ReadAloudDialog
          script={readAloud.script}
          points={readAloud.points}
          warnings={readAloud.warnings}
          onConfirm={readAloud.onConfirm}
          onCancel={readAloud.onCancel}
        />
      ) : null}

      {dialog ? (
        <ConfirmDialog
          title={dialog.title}
          body={dialog.body}
          note={dialog.note}
          confirmLabel={dialog.confirmLabel}
          onConfirm={dialog.onConfirm}
          onCancel={dialog.onCancel}
        />
      ) : null}
    </PageShell>
  );
};

export default DirectBookingPage;
